import React, { useRef, useEffect } from 'react'
import { useGetContentImageQuery } from '../../services/contentApi'
import { useAppSelector } from '../../store/hooks'
import { selectAllBoundingBoxes } from '../../store/slices/annotationSlice'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useGetTagsQuery } from '../../services/tagApi'
// import { getContrastYIQ } from '../../utils'
import { useGetProjectQuery } from '../../services/projectsApi'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { migrateBoundingBoxToPoints, getMinMaxFromPoints } from '../../utils/helpers'

interface AALSnippetProps {
  contentId: string
  bbox: AnnotationDataBoundingBox
  style?: React.CSSProperties
  rotateLongSideVertical?: boolean
}

const AALSnippet: React.FC<AALSnippetProps> = ({
  contentId,
  bbox,
  style,
  rotateLongSideVertical = false,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const allBoundingBoxes = useAppSelector(selectAllBoundingBoxes)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [imageCacheTimestamp, setImageCacheTimestamp] = React.useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  React.useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentId,
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    { skip: !contentId || !selectedProjectId || !project?.datasetid }
  )

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  useEffect(() => {
    if (!imageUrl || !bbox || !allBoundingBoxes || bbox.class_index >= allBoundingBoxes.length) {
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = imageUrl

    img.onload = () => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Migrate bounding box to ensure it has points with image dimensions
      const migratedBbox = migrateBoundingBoxToPoints(bbox, img.naturalWidth, img.naturalHeight)

      const tag = tags?.tags?.find((t) => t.id === migratedBbox.name)
      const tagColor = tag
        ? `#${tag?.color.toString(16).padStart(6, '0').slice(0, 6)}`
        : 'rgba(255, 0, 0, 0.8)'

      // Calculate the center and dimensions of the bounding box in pixel coordinates
      let bboxCenterX: number, bboxCenterY: number, bboxWidth: number, bboxHeight: number

      if (migratedBbox.points && migratedBbox.points.length === 8) {
        // Points are already in pixel coordinates, calculate center and dimensions directly
        const xCoords = [
          migratedBbox.points[0],
          migratedBbox.points[2],
          migratedBbox.points[4],
          migratedBbox.points[6],
        ]
        const yCoords = [
          migratedBbox.points[1],
          migratedBbox.points[3],
          migratedBbox.points[5],
          migratedBbox.points[7],
        ]
        bboxCenterX = (Math.min(...xCoords) + Math.max(...xCoords)) / 2
        bboxCenterY = (Math.min(...yCoords) + Math.max(...yCoords)) / 2

        // Get axis-aligned bounding rectangle for zoom calculation
        const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(migratedBbox.points)
        bboxWidth = xmax - xmin
        bboxHeight = ymax - ymin
      } else {
        // Fallback to legacy calculation - coordinates are in pixels
        const xmin = migratedBbox.xmin ?? 0
        const xmax = migratedBbox.xmax ?? 0
        const ymin = migratedBbox.ymin ?? 0
        const ymax = migratedBbox.ymax ?? 0

        bboxCenterX = (xmin + xmax) / 2
        bboxCenterY = (ymin + ymax) / 2
        bboxWidth = xmax - xmin
        bboxHeight = ymax - ymin
      }

      // Calculate the zoom factor needed to make the bounding box content fit the canvas
      // Add some padding (20%) around the bounding box
      const padding = 1.2
      const zoomX = canvas.width / (bboxWidth * padding)
      const zoomY = canvas.height / (bboxHeight * padding)
      const zoom = Math.min(zoomX, zoomY)

      // Calculate the translation needed to center the bounding box
      const translateX = canvas.width / 2 - bboxCenterX * zoom
      const translateY = canvas.height / 2 - bboxCenterY * zoom

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Apply transformations
      ctx.save()
      // If rotate is requested (OBB), rotate the image about the bbox center first
      if (rotateLongSideVertical && migratedBbox.points && migratedBbox.points.length === 8) {
        // Convert points to pixel coordinates if normalized
        const maxX = Math.max(
          migratedBbox.points[0],
          migratedBbox.points[2],
          migratedBbox.points[4],
          migratedBbox.points[6]
        )
        const maxY = Math.max(
          migratedBbox.points[1],
          migratedBbox.points[3],
          migratedBbox.points[5],
          migratedBbox.points[7]
        )
        const areNormalized = maxX <= 1 && maxY <= 1
        const pixelPoints: number[] = []
        for (let i = 0; i < migratedBbox.points.length; i += 2) {
          const px = areNormalized
            ? migratedBbox.points[i] * img.naturalWidth
            : migratedBbox.points[i]
          const py = areNormalized
            ? migratedBbox.points[i + 1] * img.naturalHeight
            : migratedBbox.points[i + 1]
          pixelPoints.push(px, py)
        }
        const xCoords = [pixelPoints[0], pixelPoints[2], pixelPoints[4], pixelPoints[6]]
        const yCoords = [pixelPoints[1], pixelPoints[3], pixelPoints[5], pixelPoints[7]]
        const centerX = (Math.min(...xCoords) + Math.max(...xCoords)) / 2
        const centerY = (Math.min(...yCoords) + Math.max(...yCoords)) / 2

        // Determine rotation required to make long edge vertical
        const dx = pixelPoints[2] - pixelPoints[0]
        const dy = pixelPoints[3] - pixelPoints[1]
        const topEdgeLen = Math.hypot(dx, dy)
        const leftDx = pixelPoints[6] - pixelPoints[0]
        const leftDy = pixelPoints[7] - pixelPoints[1]
        const leftEdgeLen = Math.hypot(leftDx, leftDy)
        const angle = Math.atan2(dy, dx)
        let rotation = topEdgeLen >= leftEdgeLen ? Math.PI / 2 - angle : -angle

        // Compute rotated coordinates relative to center to assess top-most corner
        const cos = Math.cos(rotation)
        const sin = Math.sin(rotation)
        const relPoints: Array<{ rx: number; ry: number }> = []
        for (let i = 0; i < pixelPoints.length; i += 2) {
          const rx = (pixelPoints[i] - centerX) * cos - (pixelPoints[i + 1] - centerY) * sin
          const ry = (pixelPoints[i] - centerX) * sin + (pixelPoints[i + 1] - centerY) * cos
          relPoints.push({ rx, ry })
        }
        let minIdx = 0
        for (let i = 1; i < 4; i++) {
          if (relPoints[i].ry < relPoints[minIdx].ry) minIdx = i
        }
        // Ensure point 1 (index 0) is the top-most by flipping 180deg if needed
        if (minIdx !== 0) {
          rotation += Math.PI
          const cos2 = Math.cos(Math.PI)
          const sin2 = Math.sin(Math.PI)
          for (let i = 0; i < relPoints.length; i++) {
            const x0 = relPoints[i].rx
            const y0 = relPoints[i].ry
            relPoints[i].rx = x0 * cos2 - y0 * sin2
            relPoints[i].ry = x0 * sin2 + y0 * cos2
          }
        }

        // Effective dimensions from rotated extents to avoid clipping
        let minRx = relPoints[0].rx
        let maxRx = relPoints[0].rx
        let minRy = relPoints[0].ry
        let maxRy = relPoints[0].ry
        for (let i = 1; i < 4; i++) {
          if (relPoints[i].rx < minRx) minRx = relPoints[i].rx
          if (relPoints[i].rx > maxRx) maxRx = relPoints[i].rx
          if (relPoints[i].ry < minRy) minRy = relPoints[i].ry
          if (relPoints[i].ry > maxRy) maxRy = relPoints[i].ry
        }
        const effWidth = maxRx - minRx
        const effHeight = maxRy - minRy
        const zoomXr = canvas.width / (effWidth * padding)
        const zoomYr = canvas.height / (effHeight * padding)
        const zoomR = Math.min(zoomXr, zoomYr)
        const marginCanvasY = Math.max(0, (canvas.height - effHeight * zoomR) / 2)
        // Compute current top Y in canvas space and needed delta to align to margin
        const yMinCanvas = canvas.height / 2 + zoomR * minRy
        const deltaCanvasY = marginCanvasY - yMinCanvas
        const deltaImageY = deltaCanvasY / zoomR

        // Build combined transform: center -> rotate -> scale -> translate to center -> shift top to margin
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate(rotation)
        ctx.scale(zoomR, zoomR)
        ctx.translate(-centerX, -centerY)
        ctx.translate(0, deltaImageY)
        ctx.drawImage(img, 0, 0)
        ctx.restore()
        ctx.save()
        // After drawing rotated image, prepare to draw overlay points in the same transformed space
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate(rotation)
        ctx.scale(zoomR, zoomR)
        ctx.translate(-centerX, -centerY)
        ctx.translate(0, deltaImageY)
        // Keep a reference to current zoom for overlay sizing
        ;(ctx as any)._currentZoom = zoomR
      } else {
        ctx.translate(translateX, translateY)
        ctx.scale(zoom, zoom)
        ctx.drawImage(img, 0, 0)
        ctx.restore()
        ctx.save()
        ctx.translate(translateX, translateY)
        ctx.scale(zoom, zoom)
        ;(ctx as any)._currentZoom = zoom
      }

      // Calculate the transformed bounding box coordinates for the snippet
      if (migratedBbox.points && migratedBbox.points.length === 8) {
        // Points may be normalized (0-1) or pixel coordinates; auto-detect
        const maxX = Math.max(
          migratedBbox.points[0],
          migratedBbox.points[2],
          migratedBbox.points[4],
          migratedBbox.points[6]
        )
        const maxY = Math.max(
          migratedBbox.points[1],
          migratedBbox.points[3],
          migratedBbox.points[5],
          migratedBbox.points[7]
        )
        const areNormalized = maxX <= 1 && maxY <= 1

        // Convert points to pixel-space for this canvas
        const pixelPoints: number[] = []
        for (let i = 0; i < migratedBbox.points.length; i += 2) {
          const px = areNormalized
            ? migratedBbox.points[i] * img.naturalWidth
            : migratedBbox.points[i]
          const py = areNormalized
            ? migratedBbox.points[i + 1] * img.naturalHeight
            : migratedBbox.points[i + 1]
          pixelPoints.push(px, py)
        }

        // Draw the bounding box directly in current transformed space
        ctx.strokeStyle = tagColor
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(pixelPoints[0], pixelPoints[1]) // top-left
        ctx.lineTo(pixelPoints[2], pixelPoints[3]) // top-right
        ctx.lineTo(pixelPoints[4], pixelPoints[5]) // bottom-right
        ctx.lineTo(pixelPoints[6], pixelPoints[7]) // bottom-left
        ctx.closePath()
        ctx.stroke()

        // Indicators removed
      } else {
        // Fallback for boxes without points - handle both coordinate systems
        let xmin = migratedBbox.xmin ?? 0
        let xmax = migratedBbox.xmax ?? 0
        let ymin = migratedBbox.ymin ?? 0
        let ymax = migratedBbox.ymax ?? 0

        // If values are between 0-1, assume they're normalized and convert to pixels
        if (xmax <= 1 && ymax <= 1) {
          xmin = xmin * img.naturalWidth
          xmax = xmax * img.naturalWidth
          ymin = ymin * img.naturalHeight
          ymax = ymax * img.naturalHeight
        }

        // Coordinates are now in current transformed space
        const x1 = xmin
        const y1 = ymin
        const x2 = xmax
        const y2 = ymax

        ctx.strokeStyle = tagColor
        ctx.lineWidth = 2
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)

        // Indicators removed
      }

      // Snippet thumbnails intentionally omit label text; pills are shown below.

      ctx.restore()
    }
  }, [imageUrl, allBoundingBoxes, bbox, tags?.tags, rotateLongSideVertical])

  return (
    <canvas
      ref={canvasRef}
      width={512}
      height={512}
      style={{
        display: 'block',
        objectFit: 'contain',
        ...style,
      }}
    />
  )
}

export default AALSnippet
